import React, { memo, useContext, useMemo } from 'react';
import { Field, FormikProps } from 'formik';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import RenderInput from '../../../../components/RenderInput';
import AutocompleteByOptionId from '../../../../components/AutocompleteByOptionId';
import TextFieldsOnBlur from '../../../../components/TextFieldsOnBlur';
import { getDataOptions } from '../../../../utils/arrayUtils';
import { Context as BankAccountsContext } from '../../../../api/bankAccounts/context';

type FormProps<TValues = any> = {
    formProps: FormikProps<TValues>;
    showRateOverrideCheckbox?: boolean;
    /** Show the rate-override checkbox but disable it, for forms where the rate date is driven elsewhere. */
    rateOverrideDisabled?: boolean;
    /** Tooltip explaining why the rate-override checkbox is disabled. */
    rateOverrideDisabledTooltip?: string;
};

const RemittanceBankTransferSubForm = (props: FormProps) => {
    const {
        formProps,
        showRateOverrideCheckbox = false,
        rateOverrideDisabled = false,
        rateOverrideDisabledTooltip = ''
    } = props;
    const { setFieldValue, isSubmitting, values } = formProps;

    const bankAccounts = useContext(BankAccountsContext);
    const bankAccountsOptions = useMemo(() => {
        if (!values?.currency) return [];
        const currencyId = values.currency._id || values.currency;
        return getDataOptions(bankAccounts.data)
            .filter((account) => {
                const accountCurrencyId =
                    typeof account.currency === 'object' ? account.currency?._id : account.currency;
                return accountCurrencyId == currencyId;
            })
            .map((bankAccount) => ({ ...bankAccount, name: bankAccount.accountName }));
    }, [values.currency, bankAccounts.data]);

    return (
        <Box>
            <Box pb={1}>
                <Field
                    component={AutocompleteByOptionId}
                    label="Bank"
                    name="paymentMethodObject.remittanceTransferringBank"
                    disabled={isSubmitting}
                    options={bankAccountsOptions}
                    onChange={setFieldValue}
                    fullOption
                    fullWidth
                    required
                />
            </Box>
            <TextFieldsOnBlur
                fields={[
                    {
                        name: 'paymentMethodObject.remittanceReceivingBank',
                        label: 'Receiving Bank',
                        fullWidth: true,
                        disabled: isSubmitting
                    },
                    {
                        name: 'paymentMethodObject.branch',
                        label: 'Branch',
                        fullWidth: true,
                        disabled: isSubmitting
                    },
                    {
                        name: 'paymentMethodObject.account',
                        label: 'Account',
                        fullWidth: true,
                        disabled: isSubmitting
                    },
                    {
                        name: 'paymentMethodObject.iban',
                        label: 'IBAN',
                        fullWidth: true,
                        disabled: isSubmitting
                    },
                    {
                        name: 'paymentMethodObject.swiftCode',
                        label: 'SWIFT Code',
                        fullWidth: true,
                        disabled: isSubmitting
                    },
                    {
                        name: 'paymentMethodObject.reference',
                        label: 'Reference',
                        fullWidth: true,
                        disabled: isSubmitting,
                        required: true
                    }
                ]}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Field
                        component={RenderInput}
                        name="paymentMethodObject.valueDate"
                        type="date"
                        label="Value Date"
                        placeholder="__/__/____"
                        mask="__/__/____"
                        format="dd/MM/yyyy"
                        autoOk
                        disabled={isSubmitting}
                        fullWidth
                        required
                    />
                </Box>
                {showRateOverrideCheckbox && (
                    <Box sx={{ flexShrink: 0 }}>
                        <Tooltip
                            title={rateOverrideDisabled ? rateOverrideDisabledTooltip : ''}
                            placement="top"
                            arrow
                        >
                            <span style={{ display: 'block' }}>
                                <Field
                                    type="checkbox"
                                    component={RenderInput}
                                    name="paymentMethodObject.overrideRateByValueDate"
                                    label="Override rate by value date"
                                    disabled={isSubmitting || rateOverrideDisabled}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default memo(RemittanceBankTransferSubForm);
